import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  StarIcon,
  AcademicCapIcon,
  ClockIcon,
  UsersIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  TrophyIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

interface Job {
  id: string;
  title: string;
  description: string;
  requirements: string[];
  skills: string[];
  location: string;
  remoteType: string;
  jobType: string;
  experienceLevel: string;
  salaryRange: {
    min: number;
    max: number;
    currency: string;
  };
  // Optional because the API withholds it from signed-out viewers. The modal
  // is behind auth, so in practice it is always present here.
  applicationsCount?: number;
  viewsCount: number;
  employerName: string;
  autoOffer?: {
    enabled: boolean;
    timeLimit: number;
    requiredCourses: Array<{
      courseId: string;
      courseName: string;
      minimumGrade: number;
    }>;
    minimumOverallScore?: number;
    createdAt: Date;
  };
  autoOfferDeadline?: string | Date;
}

interface MatchAnalysis {
  overallMatch: number;
  skillsMatch: {
    matched: string[];
    missing: string[];
    matchPercentage: number;
  };
  experienceMatch: {
    meets: boolean;
    userLevel: string;
    requiredLevel: string;
  };
  coursesRecommended: {
    id: string;
    title: string;
    reason: string;
    estimatedTime: string;
  }[];
  assessments: {
    id: string;
    title: string;
    skill: string;
    estimatedTime: string;
    potentialBoost: number;
  }[];
  likelihood: 'high' | 'medium' | 'low';
  competitionLevel: 'high' | 'medium' | 'low';
}

interface JobApplicationModalProps {
  job: Job;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const JobApplicationModal: React.FC<JobApplicationModalProps> = ({
  job,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { currentUser, userProfile } = useAuth();
  const [step, setStep] = useState<'analysis' | 'courses' | 'assessment' | 'application'>('analysis');
  const [matchAnalysis, setMatchAnalysis] = useState<MatchAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedAssessments, setSelectedAssessments] = useState<string[]>([]);
  const [applicationData, setApplicationData] = useState({
    coverLetter: '',
    additionalNotes: ''
  });
  const [userPlan, setUserPlan] = useState<any>(null);
  const [showAIEnhancement, setShowAIEnhancement] = useState(false);
  const [aiEnhancing, setAiEnhancing] = useState(false);
  const [autoOfferStatus, setAutoOfferStatus] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    if (isOpen && job) {
      fetchMatchAnalysis();
      fetchAutoOfferStatus();
      if (currentUser) {
        fetchUserPlan();
      }
    }
  }, [isOpen, job, currentUser]);

  // Update timer for auto-offer
  useEffect(() => {
    if (autoOfferStatus?.deadline) {
      const updateTimer = () => {
        const deadline = new Date(autoOfferStatus.deadline);
        const now = new Date();
        const timeDiff = deadline.getTime() - now.getTime();
        
        if (timeDiff <= 0) {
          setTimeRemaining('Auto-offer period ended');
          return;
        }
        
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) {
          setTimeRemaining(`${days}d ${hours}h ${minutes}m remaining`);
        } else if (hours > 0) {
          setTimeRemaining(`${hours}h ${minutes}m remaining`);
        } else {
          setTimeRemaining(`${minutes}m remaining`);
        }
      };
      
      updateTimer();
      const interval = setInterval(updateTimer, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, [autoOfferStatus]);

  const fetchMatchAnalysis = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${job.id}/match-analysis`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setMatchAnalysis(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch match analysis');
      }
    } catch (error: any) {
      console.error('Error fetching match analysis:', error);
      toast.error('Failed to analyze job match');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPlan = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/subscriptions/my-plan`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserPlan(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching user plan:', error);
    }
  };

  const fetchAutoOfferStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${job.id}/auto-offer-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.autoOfferStatus) {
          setAutoOfferStatus(result.data.autoOfferStatus);
        }
      }
    } catch (error: any) {
      console.error('Error fetching auto-offer status:', error);
    }
  };

  const handleAIEnhancement = async () => {
    const hasPaidAccess = userPlan && (userPlan.tier === 'plus' || userPlan.tier === 'pro');
    
    if (!hasPaidAccess) {
      toast.error('AI CV Enhancement is available for Plus and Pro subscribers only!');
      setShowAIEnhancement(false);
      return;
    }

    try {
      setAiEnhancing(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/ai/enhance-cv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          jobId: job.id,
          currentCoverLetter: applicationData.coverLetter,
          userProfile,
          jobDetails: job
        })
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.upgradeRequired) {
          toast.error(error.message);
          return;
        }
        throw new Error(error.message || 'Failed to enhance CV');
      }

      const result = await response.json();
      
      if (result.success) {
        setApplicationData(prev => ({
          ...prev,
          coverLetter: result.data.enhancedCoverLetter
        }));
        toast.success('Your cover letter has been enhanced with AI!');
        setShowAIEnhancement(false);
      } else {
        throw new Error(result.message || 'Failed to enhance CV');
      }
    } catch (error: any) {
      console.error('Error enhancing CV:', error);
      toast.error('Failed to enhance CV with AI');
    } finally {
      setAiEnhancing(false);
    }
  };

  const handleTakeAssessment = async (assessmentId: string) => {
    try {
      setLoading(true);
      
      // Start the job-specific assessment
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/job-assessments/${assessmentId}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // In a real implementation, this would open the assessment interface
        // For now, we'll simulate completion and update the UI
        toast.success('Assessment started! Redirecting to assessment interface...');
        
        // Simulate assessment completion after a delay
        setTimeout(async () => {
          try {
            await fetchMatchAnalysis();
            toast.success('Assessment completed! Your match score has been updated.');
          } catch (error) {
            console.error('Error updating match analysis:', error);
          }
        }, 3000);
        
        // Add to selected assessments
        setSelectedAssessments(prev => [...prev, assessmentId]);
      } else {
        throw new Error(result.message || 'Failed to start assessment');
      }
    } catch (error: any) {
      console.error('Error taking assessment:', error);
      if (error.message.includes('Maximum attempts exceeded')) {
        toast.error('You have reached the maximum number of attempts for this assessment.');
      } else {
        toast.error('Failed to start assessment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    try {
      setLoading(true);
      
      const applicationPayload = {
        coverLetter: applicationData.coverLetter,
        additionalNotes: applicationData.additionalNotes,
        completedAssessments: selectedAssessments,
        userCourses: userProfile?.completedCourses || [],
        userGrades: userProfile?.courseStats || {}
      };

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/${job.id}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(applicationPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Application submitted successfully!');
        onSuccess?.();
        onClose();
      } else {
        throw new Error(result.message || 'Failed to submit application');
      }
    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const getLikelihoodColor = (likelihood: string) => {
    switch (likelihood) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getCompetitionColor = (level: string) => {
    switch (level) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Apply for {job.title}</h2>
            <p className="text-sm text-gray-600">{job.employerName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Auto-Offer Banner */}
        {autoOfferStatus?.enabled && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 rounded-full p-2">
                  <CheckCircleIcon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">🚀 Auto-Offer Active!</h3>
                  <p className="text-green-100 text-sm">
                    Top qualifying candidate will be automatically approved
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-white">
                  <ClockIcon className="h-5 w-5" />
                  <span className="font-bold text-lg">
                    {timeRemaining || 'Calculating...'}
                  </span>
                </div>
                <p className="text-green-100 text-xs mt-1">
                  Time remaining for auto-selection
                </p>
              </div>
            </div>
            
            {autoOfferStatus.userEligibility && (
              <div className="mt-3 p-3 bg-white bg-opacity-10 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {autoOfferStatus.userEligibility.eligible ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-200" />
                    ) : (
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-200" />
                    )}
                    <span className="font-medium">
                      {autoOfferStatus.userEligibility.eligible ? 'You\'re Eligible!' : 'Requirements Not Met'}
                    </span>
                  </div>
                  <span className="text-green-100 font-bold">
                    Your Score: {autoOfferStatus.userEligibility.score}%
                  </span>
                </div>
                
                {!autoOfferStatus.userEligibility.eligible && (
                  <div className="mt-2 text-sm text-green-100">
                    {autoOfferStatus.userEligibility.missingCourses?.length > 0 && (
                      <div>Missing: {autoOfferStatus.userEligibility.missingCourses.join(', ')}</div>
                    )}
                    {autoOfferStatus.userEligibility.lowGradeCourses?.length > 0 && (
                      <div>Need higher grades: {autoOfferStatus.userEligibility.lowGradeCourses.join(', ')}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loading && !matchAnalysis ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Analyzing your match...</p>
          </div>
        ) : (
          <div className="p-6">
            {/* Step Navigation */}
            <div className="flex mb-6 border-b">
              {[
                { key: 'analysis', label: 'Match Analysis', icon: StarIcon },
                { key: 'courses', label: 'Recommended Courses', icon: AcademicCapIcon },
                { key: 'assessment', label: 'Assessments', icon: TrophyIcon },
                { key: 'application', label: 'Application', icon: DocumentTextIcon }
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setStep(key as any)}
                  className={`flex items-center px-4 py-2 border-b-2 transition-colors ${
                    step === key
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {label}
                </button>
              ))}
            </div>

            {/* Step Content */}
            {step === 'analysis' && matchAnalysis && (
              <div className="space-y-6">
                {/* Overall Match Score */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Your Match Score</h3>
                      <p className="text-sm text-gray-600">Based on your profile and experience</p>
                    </div>
                    <div className="text-right">
                      <div className="text-4xl font-bold text-indigo-600">
                        {matchAnalysis.overallMatch}%
                      </div>
                      <div className={`text-sm font-medium ${getLikelihoodColor(matchAnalysis.likelihood)}`}>
                        {matchAnalysis.likelihood.toUpperCase()} likelihood
                      </div>
                    </div>
                  </div>
                </div>

                {/* Competition Analysis */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white border rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <UsersIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-600">Total Applicants</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{job.applicationsCount}</div>
                  </div>
                  
                  <div className="bg-white border rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-600">Views</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{job.viewsCount}</div>
                  </div>
                  
                  <div className="bg-white border rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <TrophyIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-600">Competition</span>
                    </div>
                    <div className={`text-2xl font-bold ${getCompetitionColor(matchAnalysis.competitionLevel)}`}>
                      {matchAnalysis.competitionLevel.toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* Skills Analysis */}
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Skills Match</h4>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Skills Match</span>
                      <span>{matchAnalysis.skillsMatch.matchPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${matchAnalysis.skillsMatch.matchPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-green-600 mb-2 flex items-center">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Matched Skills ({matchAnalysis.skillsMatch.matched.length})
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {matchAnalysis.skillsMatch.matched.map((skill, index) => (
                          <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-red-600 mb-2 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Missing Skills ({matchAnalysis.skillsMatch.missing.length})
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {matchAnalysis.skillsMatch.missing.map((skill, index) => (
                          <span key={index} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between">
                  <div className="flex space-x-3">
                    {matchAnalysis.coursesRecommended.length > 0 && (
                      <button
                        onClick={() => setStep('courses')}
                        className="px-4 py-2 border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50"
                      >
                        View Recommended Courses
                      </button>
                    )}
                    {matchAnalysis.assessments.length > 0 && (
                      <button
                        onClick={() => setStep('assessment')}
                        className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50"
                      >
                        Take Assessments
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => setStep('application')}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                  >
                    Apply Now
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Courses Step */}
            {step === 'courses' && matchAnalysis && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Recommended Courses</h3>
                  <p className="text-gray-600">Complete these courses to improve your match score</p>
                </div>
                
                {matchAnalysis.coursesRecommended.map((course) => (
                  <div key={course.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{course.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{course.reason}</p>
                        <div className="flex items-center mt-2 text-sm text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {course.estimatedTime}
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          window.open(`/courses/${course.id}`, '_blank');
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 ml-4"
                      >
                        Start Course
                      </button>
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setStep('analysis')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back to Analysis
                  </button>
                  <button
                    onClick={() => setStep('application')}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Continue to Application
                  </button>
                </div>
              </div>
            )}

            {/* Assessment Step */}
            {step === 'assessment' && matchAnalysis && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Skill Assessments</h3>
                  <p className="text-gray-600">Take these assessments to boost your match score</p>
                </div>
                
                {matchAnalysis.assessments.map((assessment) => (
                  <div key={assessment.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <h4 className="font-medium text-gray-900">{assessment.title}</h4>
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            +{assessment.potentialBoost}% match
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Skill: {assessment.skill}</p>
                        <div className="flex items-center mt-2 text-sm text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {assessment.estimatedTime}
                        </div>
                      </div>
                      <button
                        onClick={() => handleTakeAssessment(assessment.id)}
                        disabled={loading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 ml-4 disabled:opacity-50"
                      >
                        {loading ? 'Taking...' : 'Take Assessment'}
                      </button>
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setStep('analysis')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back to Analysis
                  </button>
                  <button
                    onClick={() => setStep('application')}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Continue to Application
                  </button>
                </div>
              </div>
            )}

            {/* Application Step */}
            {step === 'application' && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Submit Your Application</h3>
                  <p className="text-gray-600">Complete your application for {job.title}</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Cover Letter *
                      </label>
                      {userPlan && (userPlan.tier === 'plus' || userPlan.tier === 'pro') ? (
                        <button
                          onClick={() => setShowAIEnhancement(true)}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full hover:from-purple-700 hover:to-indigo-700"
                        >
                          <SparklesIcon className="h-3 w-3 mr-1" />
                          AI Enhance
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          AI Enhancement (Pro Feature)
                        </span>
                      )}
                    </div>
                    <textarea
                      value={applicationData.coverLetter}
                      onChange={(e) => setApplicationData({ ...applicationData, coverLetter: e.target.value })}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                    />
                  </div>

                  {/* Completed Courses Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Completed Courses
                    </label>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {userProfile?.completedCourses && userProfile.completedCourses.length > 0 ? (
                        <div className="space-y-2">
                          {userProfile.completedCourses.map((course: any, index: number) => (
                            <div key={index} className="flex items-center justify-between bg-white rounded-md p-3 border">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{course.title || course.name}</h4>
                                <p className="text-xs text-gray-500">{course.provider || 'Platform Course'}</p>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium text-green-600">
                                  {course.grade || course.score || '90'}%
                                </div>
                                <div className="text-xs text-gray-400">
                                  {course.completedAt ? new Date(course.completedAt).toLocaleDateString() : 'Completed'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 py-6">
                          <AcademicCapIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm">No completed courses to display</p>
                          <p className="text-xs text-gray-400 mt-1">Complete courses to showcase your skills to employers</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Course Statistics */}
                  {userProfile?.courseStats && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Learning Statistics
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-blue-600">{userProfile.courseStats.completed || 0}</div>
                          <div className="text-xs text-blue-600">Completed</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-green-600">{userProfile.courseStats.certificationsEarned || 0}</div>
                          <div className="text-xs text-green-600">Certified</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-purple-600">{userProfile.courseStats.averageScore || 85}%</div>
                          <div className="text-xs text-purple-600">Avg Score</div>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-orange-600">{userProfile.courseStats.enrolled || 0}</div>
                          <div className="text-xs text-orange-600">In Progress</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      value={applicationData.additionalNotes}
                      onChange={(e) => setApplicationData({ ...applicationData, additionalNotes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Any additional information you'd like to share..."
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setStep('analysis')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Back to Analysis
                  </button>
                  <button
                    onClick={handleSubmitApplication}
                    disabled={loading || !applicationData.coverLetter.trim()}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* AI Enhancement Modal */}
      {showAIEnhancement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <SparklesIcon className="h-5 w-5 mr-2 text-purple-600" />
                AI CV Enhancement
              </h3>
              <button
                onClick={() => setShowAIEnhancement(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Our AI will analyze your profile and this job posting to create a personalized, compelling cover letter that highlights your most relevant qualifications.
              </p>
              
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-start">
                  <TrophyIcon className="h-5 w-5 text-purple-600 mr-2 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-purple-900">What AI Enhancement Includes:</h4>
                    <ul className="text-sm text-purple-800 mt-1 space-y-1">
                      <li>• Personalized content based on your courses and skills</li>
                      <li>• Industry-specific language and keywords</li>
                      <li>• Professional tone and structure</li>
                      <li>• Highlights your strongest qualifications</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowAIEnhancement(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAIEnhancement}
                disabled={aiEnhancing}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 flex items-center justify-center"
              >
                {aiEnhancing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enhancing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Enhance with AI
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplicationModal;